import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../i18n/idioma';
import { useFechar } from '../input/useAtalhos';
import type { DueloEstado } from '../world/types';
import { DueloCentro } from './DueloCentro';
import { DueloPlacar } from './DueloPlacar';
import { prazoNoCliente, relogio } from './dueloTempo';

interface Props {
  estado: DueloEstado;
  onDesistir(): void;
  onSair(): void;
}

export function DueloHud({ estado, onDesistir, onSair }: Props) {
  const marca = useRef({ chave: '', luta: 0, rodada: 0, saque: 0, ausente: 0, carregar: 0 });
  const chave = `${estado.estado}|${estado.lutaComecaEmMs}|${estado.rodadaTerminaEmMs}|${estado.saqueAteMs}|${estado.ausenteAteMs}|${estado.carregarAteMs}|${estado.agoraServidorMs}`;
  if (marca.current.chave !== chave) {
    marca.current = {
      chave,
      luta: prazoNoCliente(estado.lutaComecaEmMs, estado.agoraServidorMs),
      rodada: prazoNoCliente(estado.rodadaTerminaEmMs, estado.agoraServidorMs),
      saque: prazoNoCliente(estado.saqueAteMs, estado.agoraServidorMs),
      ausente: prazoNoCliente(estado.ausenteAteMs, estado.agoraServidorMs),
      carregar: prazoNoCliente(estado.carregarAteMs, estado.agoraServidorMs)
    };
  }

  const [agora, setAgora] = useState(() => Date.now());
  const [confirma, setConfirma] = useState<'desistir' | 'sair' | null>(null);
  const [fala, setFala] = useState('');
  useEffect(() => {
    const id = window.setInterval(() => setAgora(Date.now()), 200);
    return () => window.clearInterval(id);
  }, []);

  const faltaLuta = marca.current.luta - agora;
  const faltaRodada = marca.current.rodada - agora;
  const faltaSaque = marca.current.saque - agora;
  const faltaAusente = marca.current.ausente - agora;
  const faltaCarregar = marca.current.carregar - agora;

  const fase = useRef({ estado: estado.estado, luteAte: 0 });
  if (estado.estado === 'contagem' && faltaLuta > 0)
    fase.current.luteAte = 0;
  else if (estado.estado === 'contagem' && faltaLuta <= 0 && fase.current.luteAte === 0)
    fase.current.luteAte = Date.now() + 900;
  else if (fase.current.estado === 'contagem' && estado.estado === 'luta' && fase.current.luteAte === 0)
    fase.current.luteAte = Date.now() + 900;
  if (fase.current.estado !== estado.estado) fase.current.estado = estado.estado;
  const lute = agora < fase.current.luteAte;

  const faixaRef = useRef({ n: estado.rodadas.length, ate: 0 });
  if (estado.rodadas.length > faixaRef.current.n) {
    const dur = estado.estado === 'contagem'
      ? Math.max(1000, Math.min(2500, (estado.contagemMs || 0) - 2000))
      : 2500;
    faixaRef.current = { n: estado.rodadas.length, ate: Date.now() + dur };
  }
  const faixa = agora < faixaRef.current.ate
    && (estado.estado === 'contagem' || estado.estado === 'fim' || estado.estado === 'saque');

  const pips = useRef({ voce: estado.placarVoce, ele: estado.placarEle, novo: '' as '' | 'voce' | 'ele', ate: 0 });
  if (estado.placarVoce !== pips.current.voce || estado.placarEle !== pips.current.ele) {
    const novo = estado.placarVoce > pips.current.voce ? 'voce' as const
      : estado.placarEle > pips.current.ele ? 'ele' as const : '';
    pips.current = {
      voce: estado.placarVoce,
      ele: estado.placarEle,
      novo,
      ate: novo ? Date.now() + 1800 : 0
    };
  } else if (pips.current.novo && agora > pips.current.ate) {
    pips.current.novo = '';
  }

  const barra = estado.modo === 'com_loot' && estado.vencedor === 'voce'
    && (estado.estado === 'saque' || (estado.estado === 'fim' && estado.desfecho === 'vitoria'));
  const saiSemVer = estado.desfecho === 'abandono'
    || estado.desfecho === 'cancelado'
    || estado.desfecho === 'interrompido';
  const cartao = estado.estado === 'fim' && !barra && !faixa && !saiSemVer;
  const placar = estado.estado === 'contagem' || estado.estado === 'luta' || estado.estado === 'ausente';
  // Placar e centro são irmãos: com a mesma key o React duplica o placar a
  // cada render (vida, relógio) e a pilha de nós derruba o fps.
  const momento = `${estado.estado}|${estado.rodada}|${estado.rodadas.length}|${estado.modo}|${estado.desfecho}`;

  const falaChave = useRef('');
  const prevEstado = useRef(estado.estado);
  useEffect(() => {
    const voltou = prevEstado.current === 'ausente' && estado.estado === 'contagem';
    prevEstado.current = estado.estado;
    const ultima = estado.rodadas[estado.rodadas.length - 1];
    let id = '';
    let texto = '';
    if (lute) {
      id = `lute-${estado.rodada}`;
      texto = t('duelo.lute');
    } else if (faixa && ultima) {
      id = `faixa-${estado.rodadas.length}`;
      texto = ultima.quem === 'voce'
        ? t('duelo.faixa.voce', { n: estado.rodadas.length })
        : ultima.quem === 'ele'
          ? t('duelo.faixa.ele', { n: estado.rodadas.length, nome: estado.adversario })
          : t('duelo.faixa.empate', { n: estado.rodadas.length });
    } else if (estado.estado === 'contagem') {
      id = `c-${estado.rodada}`;
      texto = voltou
        ? t('duelo.fala.voltou', { nome: estado.adversario })
        : t('duelo.fala.rodada', { n: estado.rodada, s: Math.max(1, Math.ceil((estado.contagemMs || 5000) / 1000)) });
    } else if (estado.estado === 'carregando') {
      // A contagem só começa com os dois clientes carregados; o relógio é o limite antes de cancelar.
      const seg = Math.max(0, Math.ceil(faltaCarregar / 1000));
      id = `carregando-${estado.voceCarregou}-${estado.eleCarregou}-${seg}`;
      texto = estado.voceCarregou && !estado.eleCarregou
        ? t('duelo.carregando.esperando', { nome: estado.adversario, tempo: relogio(Math.max(0, faltaCarregar)) })
        : t('duelo.carregando.voce');
    } else if (estado.estado === 'ausente') {
      id = 'ausente';
      texto = t('duelo.ausenteAviso', { nome: estado.adversario, tempo: relogio(Math.max(0, faltaAusente)) });
    } else if (barra && estado.estado === 'fim') {
      id = 'saque-fim';
      texto = t('duelo.saque.acabou', { nome: estado.adversario });
    } else if (cartao) {
      id = `fim-${estado.desfecho}-${estado.vencedor}`;
      texto = estado.vencedor === 'voce'
        ? t('duelo.fim.vitoria')
        : estado.vencedor === 'ele' ? t('duelo.fim.derrota') : t('duelo.fim.empate');
    }
    if (id && id !== falaChave.current) {
      falaChave.current = id;
      setFala(texto);
    }
  }, [lute, faixa, cartao, barra, estado, faltaAusente, faltaCarregar]);

  if (estado.estado === 'aposta' || estado.estado === 'indo') return null;

  const pedirSair = () => {
    if (estado.estado === 'saque' && faltaSaque > 0) setConfirma('sair');
    else onSair();
  };

  const trilhaSaque = estado.saqueTotalMs > 0
    ? Math.max(0, Math.min(100, faltaSaque / estado.saqueTotalMs * 100))
    : 0;

  return (
    <div className="duelo-hud" role="region" aria-label={t('duelo.regiao')}>
      <div className="duelo-hud__fala" aria-live="polite">{fala}</div>
      {estado.estado === 'contagem' && <div className="vinheta" />}
      {placar && (
        <DueloPlacar
          key={`placar|${momento}`}
          estado={estado}
          faltaRodada={faltaRodada}
          faltaAusente={faltaAusente}
          faltaLuta={faltaLuta}
          entreRodadas={faixa && !lute}
          pipNovo={pips.current.novo}
          onDesistir={() => setConfirma('desistir')}
        />
      )}
      <DueloCentro
        key={`centro|${momento}`}
        estado={estado}
        faltaLuta={faltaLuta}
        faixa={faixa && !lute}
        lute={lute}
        cartao={cartao}
        onSair={onSair}
      />
      {barra && (
        <div className="fim-barra" role="group" aria-label={t('duelo.fim.vitoria')}>
          <strong>{t('duelo.fim.vitoria')} {estado.placarVoce} × {estado.placarEle}</strong>
          <span>
            {estado.estado === 'fim'
              ? t('duelo.saque.acabou', { nome: estado.adversario })
              : t('duelo.saque.barra', { nome: estado.adversario, tempo: relogio(Math.max(0, faltaSaque)) })}
          </span>
          <button type="button" className="btn btn--primary" onClick={pedirSair}>{t('duelo.sairArena')}</button>
          {estado.estado === 'saque' && (
            <span className="centro__trilho"><i style={{ width: `${trilhaSaque}%` }} /></span>
          )}
        </div>
      )}
      {confirma && createPortal(
        <DueloConfirma
          titulo={confirma === 'desistir' ? t('duelo.desistir') : t('duelo.sairArena')}
          texto={confirma === 'desistir'
            ? t(estado.comAposta ? 'duelo.desistirConfirmaNome' : 'duelo.desistirConfirmaSemAposta', { nome: estado.adversario })
            : t('duelo.saque.sairConfirma', { tempo: relogio(Math.max(0, faltaSaque)), nome: estado.adversario })}
          ficar={confirma === 'desistir' ? t('duelo.continuar') : t('comum.cancelar')}
          onFechar={() => setConfirma(null)}
          onConfirmar={() => {
            setConfirma(null);
            if (confirma === 'desistir') onDesistir();
            else onSair();
          }}
        />,
        document.body
      )}
    </div>
  );
}

function DueloConfirma({
  titulo, texto, ficar, onFechar, onConfirmar
}: {
  titulo: string;
  texto: string;
  ficar: string;
  onFechar(): void;
  onConfirmar(): void;
}) {
  const ficarRef = useRef<HTMLButtonElement>(null);
  useFechar(onFechar, { modal: true });
  useEffect(() => { ficarRef.current?.focus(); }, []);
  return (
    <div className="duelo-confirma" role="presentation">
      <div className="duelo-confirma__caixa" role="dialog" aria-modal="true" aria-labelledby="duelo-confirma-titulo">
        <h3 id="duelo-confirma-titulo">{titulo}</h3>
        <p>{texto}</p>
        <div className="duelo-confirma__acoes">
          <button ref={ficarRef} type="button" className="btn" onClick={onFechar}>{ficar}</button>
          <button type="button" className="btn btn--ghost" onClick={onConfirmar}>{titulo}</button>
        </div>
      </div>
    </div>
  );
}
